import React from "react";
import {
  Award,
  Sparkles,
  HeartHandshake,
  Users,
  ImageIcon,
  Quote,
} from "lucide-react";
import { founder, community, affiliation } from "../../mock/mock";

const ICONS = { Award, Sparkles, HeartHandshake, Users };

const Badge = ({ icon, label }) => {
  const Icon = ICONS[icon] || Sparkles;
  return (
    <span className="inline-flex items-center gap-2 bg-[#822432] text-white font-body text-xs font-semibold uppercase tracking-wide-sm px-4 py-2 rounded-full">
      <Icon size={14} strokeWidth={2.2} />
      {label}
    </span>
  );
};

const FounderPhoto = () => (
  <div className="relative">
    <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gradient-to-br from-[#F3D9DE] to-[#E8B9C2] flex items-center justify-center">
      {founder.photo ? (
        <img
          src={founder.photo}
          alt={founder.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex flex-col items-center text-[#822432]/70 px-6 text-center">
          <ImageIcon size={44} strokeWidth={1.4} />
          <p className="font-body text-sm font-medium mt-4">
            Professional photo coming soon
          </p>
        </div>
      )}
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 bg-white shadow-[0_15px_40px_rgba(130,36,50,0.18)] rounded-full px-6 py-3 flex items-center gap-2">
      <Award size={16} className="text-[#822432]" strokeWidth={2.2} />
      <span className="font-body text-xs font-bold uppercase tracking-wide-sm text-[#1A1A1A]">
        Black-Owned &amp; Woman-Owned
      </span>
    </div>
  </div>
);

const FounderBio = () => (
  <div>
    <p className="font-body text-xs font-semibold tracking-wide-sm uppercase text-[#C8102E] mb-4">
      {founder.eyebrow}
    </p>
    <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-2">
      {founder.name}
    </h2>
    <p className="font-body text-sm font-medium text-[#822432] uppercase tracking-wide-sm mb-6">
      {founder.role}
    </p>
    {founder.bio.map((p) => (
      <p
        key={p.slice(0, 24)}
        className="font-body text-[#555] text-base leading-relaxed mb-5"
      >
        {p}
      </p>
    ))}

    <div className="relative bg-white rounded-xl p-6 border-l-4 border-[#822432] mb-7">
      <Quote size={22} className="text-[#822432]/30 mb-2" strokeWidth={2} />
      <p className="font-serif-c italic text-lg md:text-xl text-[#1A1A1A] leading-snug">
        {founder.quote}
      </p>
    </div>

    <div className="flex flex-wrap gap-3">
      {founder.badges.map((b) => (
        <Badge key={b.label} icon={b.icon} label={b.label} />
      ))}
    </div>
  </div>
);

const ImpactCard = ({ point }) => {
  const Icon = ICONS[point.icon] || Users;
  return (
    <div className="bg-white rounded-2xl p-8 border border-[#F0DDE1] transition-all duration-400 hover:-translate-y-1.5 hover:shadow-[0_20px_45px_rgba(130,36,50,0.12)]">
      <div className="w-12 h-12 rounded-full bg-[#822432]/10 flex items-center justify-center mb-5">
        <Icon size={22} className="text-[#822432]" strokeWidth={2} />
      </div>
      <h3 className="font-body text-lg font-bold text-[#1A1A1A] mb-2">
        {point.title}
      </h3>
      <p className="font-body text-sm text-[#666] leading-relaxed">
        {point.text}
      </p>
    </div>
  );
};

const AffiliationCard = () => (
  <div className="mt-14 bg-[#1A1A1A] rounded-2xl px-8 py-10 md:px-12 md:py-12 flex flex-col md:flex-row items-center gap-8 md:gap-12">
    <div className="flex-shrink-0 flex items-center justify-center">
      <img
        src={affiliation.logo}
        alt="SURE Program at University of Houston"
        className="h-24 md:h-28 w-auto object-contain"
      />
    </div>
    <div className="text-center md:text-left">
      <p className="font-body text-xs font-semibold tracking-wide-sm uppercase text-[#E0A96D] mb-2">
        {affiliation.eyebrow}
      </p>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-3">
        {affiliation.heading}
      </h3>
      <p className="font-body text-sm md:text-base text-white/70 leading-relaxed max-w-2xl">
        {affiliation.text}
      </p>
    </div>
  </div>
);

const CommunityImpact = () => (
  <div className="mt-24 md:mt-32">
    <div className="text-center mb-14">
      <p className="font-body text-xs font-semibold tracking-wide-sm uppercase text-[#C8102E] mb-4">
        {community.eyebrow}
      </p>
      <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-5">
        {community.title}
      </h2>
      <p className="font-body text-[#555] text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
        {community.text}
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {community.points.map((pt) => (
        <ImpactCard key={pt.title} point={pt} />
      ))}
    </div>

    <AffiliationCard />
  </div>
);

const OriginStory = () => {
  if (!founder.story || founder.story.length === 0) return null;
  return (
    <div className="mt-20 md:mt-28 bg-white rounded-2xl px-7 py-12 md:px-16 md:py-16 border border-[#F0DDE1]">
      <div className="max-w-3xl mx-auto text-center">
        <Quote size={30} className="text-[#822432]/25 mx-auto mb-5" strokeWidth={2} />
        <p className="font-body text-xs font-semibold tracking-wide-sm uppercase text-[#C8102E] mb-4">
          Our Origin Story
        </p>
        <h2 className="font-display text-3xl md:text-5xl text-[#1A1A1A] mb-8">
          {founder.storyTitle}
        </h2>
        {founder.story.map((p) => (
          <p
            key={p.slice(0, 24)}
            className="font-serif-c text-lg md:text-2xl text-[#3a3733] leading-relaxed mb-5 italic"
          >
            {p}
          </p>
        ))}
        <p className="font-script text-3xl md:text-4xl text-[#822432] mt-8">
          {founder.name}
        </p>
      </div>
    </div>
  );
};

const FounderSection = () => {
  return (
    <section id="founder" className="bg-[#FBF5F6] py-20 md:py-28">
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <FounderPhoto />
          <FounderBio />
        </div>
        <OriginStory />
        <CommunityImpact />
      </div>
    </section>
  );
};

export default FounderSection;
